import type { ChangePlan, Readiness, VerdictRow } from '../api/client'
import { plural, t, type Key } from './dict'
import { when } from './format'
import { formats } from './locale'

/**
 * Sentences the server also writes, rendered here instead.
 *
 * `ObserveReport.Verdicts()` and `ChangePlan.Summary()` compose their prose in C#, and the TUI
 * and `guardianctl` read it from there. The plugin cannot: an `Accept-Language` would have to
 * reach a queue row the daemon processes minutes later, and localising the C# would put the
 * wording of a rule in a second place. But it does not need to — every part each sentence is
 * made of is already on the wire beside it. `readiness` travels with `fired`, `targets`, `days`
 * and the window's `entries`; `summary` travels with `isRelaxation`, `isDelayed` and
 * `effectiveFrom`. So the client assembles the sentence from the data, and the server's own
 * string stays as the TUI's.
 *
 * The English here therefore restates the C#. That is a real cost and it is the reason
 * `docs/12-plugin-i18n.md` decides this per sentence rather than as a rule: it is worth it for
 * the three a trader reads constantly, and not worth it for `plan.reasons[]`, which would mean
 * changing what `SettingsComparison` returns.
 */

/** What a rule's week amounts to — `ObserveReport.Verdicts()`. */
export function readinessReason(v: Pick<VerdictRow, 'readiness' | 'fired' | 'targets' | 'days'>, entries: number): string {
  const n = (count: number) => plural('report.entries', count, { count: formats().integer.format(count) })
  switch (v.readiness) {
    case 'Inconclusive':
      return t('report.reason.idle')
    case 'NeedsReview':
      return t('report.reason.fired', {
        fired: formats().integer.format(v.fired),
        targets: plural('report.targets', v.targets, { count: formats().integer.format(v.targets) }),
        days: plural('common.days', v.days, { count: formats().integer.format(v.days) }),
        entries: n(entries),
      })
    case 'CandidateForEnforce':
      return t('report.reason.never', { entries: n(entries) })
  }
}

const NEXT_STEP: Readonly<Record<Readiness, Key | null>> = {
  Inconclusive: null,
  NeedsReview: 'report.next.review',
  CandidateForEnforce: 'report.next.candidate',
}

export function nextStep(readiness: Readiness): string {
  const key = NEXT_STEP[readiness]
  return key === null ? '' : t(key)
}

/**
 * What happens to a config change, and when — `ChangePlan.Summary()`.
 *
 * The C# also prints how many hours the lock has left. That is dropped deliberately: computing
 * it needs a clock, this screen has no `ServerClock`, and `Date.now()` is the thing the project
 * forbids in the UI. The instant itself is what a trader acts on, and it is shown.
 */
export function changeSummary(plan: Pick<ChangePlan, 'isRelaxation' | 'isDelayed' | 'effectiveFrom'>): string {
  if (!plan.isRelaxation) return t('plan.tightening')
  return plan.isDelayed ? t('plan.timeLocked', { when: when(plan.effectiveFrom) }) : t('plan.peerApproved')
}

/**
 * The journal's group labels. These arrive as `Summary.Key`, which is data the server chose —
 * a source name, an English phrase, or a `DayOfWeek`. Each falls back to what arrived, so a
 * reworded label on the server reads as English rather than as a blank cell.
 */
const SOURCES: Readonly<Record<string, Key>> = {
  gatekeeper: 'journal.source.gatekeeper',
  terminal: 'journal.source.terminal',
  guardian: 'journal.source.guardian',
  reconcile: 'journal.source.reconcile',
}

export function sourceLabel(source: string): string {
  const key = SOURCES[source]
  return key === undefined ? source : t(key)
}

/** `TradeJournal.ByProtection()` — three fixed phrases, checked from C# by GatekeeperLocaleTests. */
const PROTECTION: Readonly<Record<string, Key>> = {
  'stop at entry': 'journal.protection.stopAtEntry',
  'opened naked': 'journal.protection.openedNaked',
  'guardian stepped in': 'journal.protection.guardianSteppedIn',
}

/** `DayOfWeek.ToString()`, which is English by construction. Intl knows every other language's. */
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

export function summaryKeyLabel(key: string): string {
  const protection = PROTECTION[key]
  if (protection !== undefined) return t(protection)

  const weekday = WEEKDAYS.indexOf(key)
  if (weekday >= 0) {
    // 2024-01-07 was a Sunday, so the index lands on the right day whatever the locale calls it.
    return new Intl.DateTimeFormat(formats().dateTime.resolvedOptions().locale, { weekday: 'long' }).format(
      new Date(Date.UTC(2024, 0, 7 + weekday)),
    )
  }

  return sourceLabel(key)
}
